import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { fetchData } from 'services/api/fetchData';
import { FormSheetType } from './FormTypes';
import { truncate } from 'services/utils/truncate';
import { formatDateTime } from 'services/utils/dateTimeUtils';
import CustomFieldsLiveEdit from 'components/forms/custom_fields/CustomFieldLiveEdit';
import '../../style/scss/modals.scss';

interface FormSheetModalProps {
    viewKey?: string
    formSheetId: number;
    jobResolution?: string;
    taskDescription?: string;
    refetchData?: () => void;
}

const FormSheetModal: React.FC<FormSheetModalProps> = ({
    viewKey,
    formSheetId,
    jobResolution,
    taskDescription,
    refetchData,
}) => {
    const { t } = useTranslation();
    const { handleLogout } = useAuthContext();
    const { userLocale, userTimezone } = useSettings();
    const { revealModal, closeModal } = useModal();
    const [formSheet, setFormSheet] = useState<FormSheetType | null>(null);

    // Fetch the form sheet and reveal modal
    useEffect(() => {
        const fetchFormSheet = async () => {
            try {
                const response = await fetchData({
                    apiUrl: `get_form_sheet/${formSheetId}`,
                    handleLogout,
                });
                setFormSheet(response);
            } catch (error) {
                console.error(error);
                setFormSheet(null);
            } finally {
                revealModal();
            }
        };
        fetchFormSheet();
    }, [formSheetId]);

    // Get and shorten the task description
    const taskDescriptionLabel = truncate(taskDescription, 35);

    // Determine wheter the form sheet is completed
    const isCompleted = formSheet?.status === 'completed';
    const completedBy = formSheet?.completed_by_user?.full_name || '-';
    const completedAt = formSheet?.completed_at ? formatDateTime(formSheet.completed_at, userLocale, userTimezone) : '-';

    return (
        <div className="form-sheet-modal">
            {formSheet && (
                <>
                    <div className="modal-header-content">
                        <div className="form-sheet-modal-header">
                            <h2>
                                {formSheet.form_name}
                                {taskDescriptionLabel && (
                                    <span className="form-sheet-task-description">
                                        {' - '}
                                        {taskDescriptionLabel}
                                    </span>
                                )}
                            </h2>
                            <span className={`status-badge ${formSheet.status}`}>
                                {t(`form_sheet.status.${formSheet.status}`)}
                            </span>
                        </div>

                        {isCompleted && (
                            <p className="form-sheet-completed-meta">
                                {t('form_sheet.general.completed_by', { completedBy: completedBy, completedAt: completedAt })}
                            </p>
                        )}

                        {jobResolution === 'finished' && (
                            <p className="readonly_label">
                                {t('form_sheet.general.readonly_label')}
                            </p>
                        )}
                    </div>
                    
                    <CustomFieldsLiveEdit
                        viewKey={viewKey}
                        data={formSheet}
                        apiObject="form_sheet"
                        itemId={formSheet.id}
                        layout="multi"
                        alignment="stacked"
                        viewInEditMode={jobResolution !== 'finished'}
                        isEditable={{ editability: jobResolution !== 'finished' }}
                        closeOnClickOutside={false}
                        onCancel={closeModal}
                        onSave={closeModal}
                        refetchData={() => {
                            if (refetchData) {
                                refetchData();
                            }
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default FormSheetModal;